@use 'style/utils';
@use './shared' as shared;
@import './variables';

$dialog-scroll-height-limit: 580px;

@keyframes dialogTransitionStart {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes dialogTransitionEnd {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.9);
  }
}

:host {
  /**
   * @prop --dialog-width: Override default container width
   * @prop --dialog-padding: Override default container padding (one value for the four sides)
   */
  --dialog-width: 580px;
  --dialog-transition-duration: var(--joy-transition-duration-default);
  
  display: block;
  position: fixed;
  opacity: 0;
  width: 0;
  height: 0;
  z-index: -1;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  box-sizing: border-box;
  outline: none;
  text-align: left;
}

:host([size="large"]) {
  --dialog-width: 780px;
}


@media screen and (max-width: #{$_mobile-breakpoint}) {
  :host {
    padding: 0;
    --dialog-width: 100% !important;
  }
}

:host([open]) {
  opacity: 1;
  height: 100%;
  z-index: var(--joy-core-z-index-dialog);
  width: 100%;

  @media screen and (min-width: #{$_mobile-breakpoint}) {
    padding: 50px 0;
  }
}

#{$CLASS}__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  background-color: var(--joy-color-overlay);

  &--open {
    width: 100%;
    height: 100%;
  }

  &___disabled {
    cursor: not-allowed;
  }
}

#{$CLASS}--wrapper-open {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  #{$CLASS} {
    animation: dialogTransitionStart var(--dialog-transition-duration) var(--joy-transition-timing-function) forwards;
  }
}


#{$CLASS} {
  $_padding: var(--dialog-padding, var(--joy-core-spacing-8));
  $_dimension: calc(100% - #{$_padding});
  $full-height: calc(100vh - #{$_padding});

  &--wrapper {
    position: static;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  background-color: white;
  width: var(--dialog-width);
  max-width: 100%;
  margin: 0 auto;
  border-radius: var(--joy-core-radius-4);
  padding: $_padding;
  box-shadow: var(--joy-core-elevation-4);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  max-height: calc(100vh - 100px);

  @media screen and (max-width: #{$_mobile-breakpoint}) {
    padding: var(--dialog-padding, var(--joy-core-spacing-6));
    height: 100vh;
    max-height: 100vh;
    max-width: 100%;
    width: 100%;
    overflow-y: auto;
    border-radius: var(--joy-core-radius-0);
  }

  &--banner {
    position: relative;
    left: calc(#{$_padding} * -1);
    top: calc(#{$_padding} * -1);
    width: calc(100% + calc(#{$_padding} * 2));
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }

  &--header {
    margin-bottom: var(--joy-core-spacing-7);

    &__title {
      padding-right: 45px;
      @include shared.headerTitleStyle();
    }
  }

  &--subheader {
    position: relative;
    @include utils.getFontProperties(primary-300);
    margin: var(--joy-core-spacing-2) 0 0 0;
    padding: 0;
  }

  &--body {
    font-size: var(--joy-font-size-primary-400);
    flex-shrink: 1;
    overflow-y: auto;
    overflow-x: visible;
    max-height: $dialog-scroll-height-limit;

    @media screen and (max-width: #{$_mobile-breakpoint}) {
      max-height: 100vh;
    }
  }

  // The id is used only to have more weight that default joy-icon css
  #joy-dialog--close {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    cursor: pointer;
    right: var(--joy-core-spacing-6);
    top: var(--joy-core-spacing-6);
    width: 40px;
    height: 40px;
    color: var(--joy-color-text-body);
    background-color: white;
    border-radius: 50%;

    @media screen and (max-width: #{$_mobile-breakpoint}) {
      right: 5px;
      top: 5px;
    }

    &:after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      width: 100%;
      height: 100%;
      opacity: 0.05;
      background-color: transparent;
      border-radius: 50%;
      transition: background-color var(--joy-transition-duration-default) var(--joy-transition-timing-function);
    }

    &:hover, &:focus {
      &:after {
        background-color: currentColor;
      }
    }
  }

  &--footer {
    position: relative;
    display: flex;
    justify-content: flex-end;
    // Footer default style

    &-filled {
      margin-top: var(--joy-core-spacing-5);
    }

    @media screen and (min-width: #{$_mobile-cta_breakpoint + 1}) {
      joy-button:last-child {
        margin-left: var(--joy-core-spacing-4);
      }
    }

    @media screen and (max-width: #{$_mobile-cta_breakpoint}) {
      flex-direction: column;
      align-items: stretch;

      joy-button:last-child:not(:first-child) {
        margin-top: var(--joy-core-spacing-4);
      }
    }
  }
}

::slotted(p) {
  margin: 0;
  padding: 0;
}

::slotted(joy-indicators) {
  justify-content: center;
  margin-top: var(--joy-core-spacing-4);
}
